import { create } from 'zustand';
import { tokenStorage } from '@/lib/auth/tokenStorage';
import { decodeJwt } from '@/lib/auth/jwt';
import type { SessionUser } from '@/shared/types/contracts';

type Status = 'loading' | 'authenticated' | 'unauthenticated';

type SessionState = {
  status: Status;
  user: SessionUser | null;
  token: string | null;
  hydrate: () => void;
  setSession: (user: SessionUser, token: string) => void;
  logout: (skipRedirect?: boolean) => void;
};

export const sessionStore = create<SessionState>((set) => ({
  status: 'loading',
  user: null,
  token: null,

  hydrate: () => {
    const token = tokenStorage.get();
    if (!token) {
      set({ status: 'unauthenticated' });
      return;
    }
    const payload = decodeJwt(token);
    if (!payload || payload.exp * 1000 < Date.now()) {
      tokenStorage.clear();
      set({ status: 'unauthenticated' });
      return;
    }
    set({
      status: 'authenticated',
      token,
      user: {
        id: payload.sub,
        email: payload.email,
        role: payload.role,
        display_name: payload.display_name,
        avatar_url: payload.avatar_url,
      },
    });
  },

  setSession: (user, token) => {
    tokenStorage.set(token);
    set({ status: 'authenticated', user, token });
  },

  logout: (skipRedirect = false) => {
    tokenStorage.clear();
    set({ status: 'unauthenticated', user: null, token: null });
    if (!skipRedirect && typeof window !== 'undefined') {
      // Dùng window.location để force reload — đảm bảo state Zustand/RQ được reset sạch
      const here = window.location.pathname + window.location.search;
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = `/login?from=${encodeURIComponent(here)}`;
      }
    }
  },
}));

// Selector hook tiện dùng
export function useSession() {
  return sessionStore((s) => ({
    status: s.status,
    user: s.user,
    token: s.token,
    logout: s.logout,
  }));
}
